import React, { FC, useEffect, useState, useCallback } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import { Loading } from '../../../components/Loading/Loading'
import { Button } from '../../../components/Button/Button'
import { GET, POST } from '../../../services/api'
import { i18n } from '../../../translate/i18n'
import ReactECharts from 'echarts-for-react'
import moment from 'moment'
import Swal from 'sweetalert2'
import { S3UsageData, ChartData, FilterParams } from './types.js'

const S3AnalyticsContent: FC = () => {
  const [loading, setLoading] = useState(false)
  // const [loadingDistribution, setLoadingDistribution] = useState(false) // Não usado no momento
  const [data, setData] = useState<S3UsageData[]>([])
  const [filteredData, setFilteredData] = useState<S3UsageData[]>([])
  const [filters, setFilters] = useState<FilterParams>({
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    pluginspaceId: '',
    method: '',
    folder: ''
  })

  // Estados para gráficos
  const [consumptionByPluginspace, setConsumptionByPluginspace] = useState<ChartData>({})
  const [consumptionByMethod, setConsumptionByMethod] = useState<ChartData>({}) // Análise de custos por método e pluginspace
  const [consumptionByDate, setConsumptionByDate] = useState<ChartData>({})
  const [costAnalysis, setCostAnalysis] = useState<ChartData>({})

  // Estado para métodos disponíveis
  const [availableMethods, setAvailableMethods] = useState<string[]>([])

  // Estado para cotação do dólar
  const [dollarRate, setDollarRate] = useState<number>(0)

  // Estados para modal de análise IA
  const [showIAModal, setShowIAModal] = useState(false)
  const [currentChartData, setCurrentChartData] = useState<any>(null)
  const [currentChartType, setCurrentChartType] = useState<string>('')
  const [iaPrompt, setIaPrompt] = useState<string>('')
  const [iaAnalysisResult, setIaAnalysisResult] = useState<string>('')
  const [iaAnalysisLoading, setIaAnalysisLoading] = useState(false)

  // Estados para funcionalidades do modal
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [speechRecognition, setSpeechRecognition] = useState<any>(null)

  // Estado para IA - removido setAiAnalysis não utilizado
  // const [aiAnalysis, setAiAnalysis] = useState<string>('')

  // Função para buscar cotação do dólar
  const fetchDollarRate = useCallback(async () => {
    try {
      console.log('Fetching dollar rate...')
      
      // Usando API pública do Banco Central do Brasil
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD')
      const data = await response.json()
      
      if (data && data.rates && data.rates.BRL) {
        const rate = data.rates.BRL
        setDollarRate(rate)
        console.log('Dollar rate loaded successfully:', rate)
      } else {
        console.warn('Dollar rate API returned unexpected format:', data)
        // Valor de fallback (aproximadamente 5.20)
        setDollarRate(5.20)
      }
    } catch (error) {
      console.error('Error fetching dollar rate:', error)
      // Valor de fallback em caso de erro
      setDollarRate(5.20)
    }
  }, []) // Sem dependências - função estável

  // Função para gerar dados mock para teste
  const generateMockData = useCallback((): S3UsageData[] => {
    const methods = ['GET', 'POST', 'PUT', 'DELETE']
    const pluginspaces = [1001, 1002, 1003, 1004, 1005]
    const mockData: S3UsageData[] = []

    // Gerar 50 registros de exemplo
    for (let i = 0; i < 50; i++) {
      const randomMethod = methods[Math.floor(Math.random() * methods.length)]
      const randomPluginspace = pluginspaces[Math.floor(Math.random() * pluginspaces.length)]
      const randomDate = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000)
      
      mockData.push({
        id: i + 1,
        method: randomMethod,
        pluginspaceId: randomPluginspace,
        total: Math.floor(Math.random() * 1000) + 100,
        folder: `folder-${i + 1}`,
        unitMenssure: 'MB',
        priceUnit: Math.random() * 0.05 + 0.01,
        dataSumarized: randomDate.toISOString().split('T')[0],
        created: randomDate.toISOString(),
        updated: randomDate.toISOString()
      })
    }

    return mockData
  }, [])

  // Função para buscar dados do S3 com filtros
  const fetchS3Data = useCallback(async () => {
    try {
      console.log('Setting loading to TRUE - fetchS3Data started')
      setLoading(true)
      console.log('Fetching S3 data from accounting-s3/filter...')
      
      // Construir parâmetros do body com base nos filtros
      const requestBody: any = {
        startDate: filters.startDate,
        endDate: filters.endDate
      }
      
      // Adicionar parâmetros opcionais apenas se fornecidos
      if (filters.method && filters.method.trim()) {
        requestBody.method = filters.method.trim()
      }
      
      if (filters.pluginspaceId && filters.pluginspaceId.trim()) {
        requestBody.pluginspaceId = filters.pluginspaceId.trim()
      }
      
      if (filters.folder && filters.folder.trim()) {
        requestBody.folder = filters.folder.trim()
      }
      
      console.log('S3 Filter Request Body:', requestBody)
      
      const response = await POST('accounting-s3/filter', requestBody)
      console.log('S3 Filter API Response:', response)
      
      if (response.success && response.data) {
        setData(response.data)
        console.log('S3 filtered data loaded successfully:', response.data)
      } else {
        console.warn('API returned success=false:', response)
        // Usar dados mock para teste se a API falhar
        const mockData = generateMockData()
        setData(mockData)
        console.log('Using mock data for testing:', mockData)
        
        Swal.fire({
          icon: 'warning',
          title: i18n.t('s3Analytics.error') as string,
          text: 'API não disponível. Usando dados de teste.',
          timer: 3000
        })
      }
    } catch (error) {
        console.error('Error fetching S3 data:', error)
      
      // Usar dados mock para teste se houver erro de rede
      const mockData = generateMockData()
      setData(mockData)
      console.log('Network error - using mock data for testing:', mockData)
      
      Swal.fire({
        icon: 'warning',
        title: i18n.t('s3Analytics.error') as string,
        text: 'Erro de rede. Usando dados de teste para demonstração.',
        timer: 3000
      })
    } finally {
      console.log('Setting loading to FALSE - fetchS3Data finished')
      setLoading(false)
    }
  }, [filters.startDate, filters.endDate, filters.method, filters.pluginspaceId, filters.folder, generateMockData])

  // Função para buscar métodos disponíveis da API
  const fetchAvailableMethods = useCallback(async () => {
    try {
      console.log('Fetching available methods from accounting-s3/methods...')
      
      // Primeira tentativa com a rota original
      let response = await GET('accounting-s3/methods')
      console.log('Methods API Response:', response)
      
      // Se falhar com erro de validação, tentar com ID genérico
      if (!response.success && response.message && response.message.includes('Validation failed')) {
        console.log('Trying alternative route with generic ID...')
        response = await GET('accounting-s3/methods/1')
        console.log('Alternative Methods API Response:', response)
      }
      
      if (response.success && response.data) {
        // Verificar se response.data é um array de strings
        if (Array.isArray(response.data)) {
          setAvailableMethods(response.data)
          console.log('Methods loaded successfully:', response.data)
        } else {
          console.warn('Methods API returned unexpected data format:', response.data)
          setAvailableMethods(['writeAccountingS3File'])
        }
      } else {
        console.warn('Methods API returned success=false:', response)
        // Usar métodos padrão se a API falhar
        setAvailableMethods(['writeAccountingS3File'])
      }
    } catch (error) {
      console.error('Error fetching methods:', error)
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
      console.error('Error details:', errorMessage)
      // Usar métodos padrão se houver erro de rede
      setAvailableMethods(['writeAccountingS3File'])
      
      // Mostrar erro específico para debug
      Swal.fire({
        icon: 'warning',
        title: 'Erro ao buscar métodos',
        text: `Erro: ${errorMessage}. Usando métodos padrão.`,
        timer: 3000
      })
    }
  }, []) // Sem dependências - função estável

  // Função para buscar distribuição de métodos da API
  /* const fetchMethodDistribution = useCallback(async () => {
    try {
      setLoadingDistribution(true)
      console.log('Fetching method distribution from accounting-s3/method-distribution...')
      
      // Construir parâmetros da query
      const params = new URLSearchParams({
        startDate: filters.startDate,
        endDate: filters.endDate
      })
      
      // Adicionar pluginspaceId apenas se fornecido
      if (filters.pluginspaceId && filters.pluginspaceId.trim()) {
        params.append('pluginspaceId', filters.pluginspaceId.trim())
      }
      
      const url = `accounting-s3/method-distribution?${params.toString()}`
      console.log('Method distribution URL:', url)
      
      const response = await GET(url)
      console.log('Method Distribution API Response:', response)
      
      if (response.success && response.data) {
        // Assumindo que a API retorna dados no formato esperado
        console.log('Method distribution loaded successfully:', response.data)
        
        // Se for um array, usar diretamente; se for objeto, extrair dados
        const distributionData = Array.isArray(response.data) ? response.data : response.data.data || []
        
        // Atualizar dados principais se a distribuição trouxer dados detalhados
        if (distributionData.length > 0) {
          setData(distributionData)
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Distribuição Carregada',
          text: `Dados de distribuição de métodos carregados com sucesso!`,
          timer: 2000
        })
      } else {
        console.warn('Method Distribution API returned success=false:', response)
        
        Swal.fire({
          icon: 'warning',
          title: 'Aviso',
          text: 'Nenhum dado de distribuição encontrado para os filtros selecionados.',
          timer: 3000
        })
      }
    } catch (error) {
      console.error('Error fetching method distribution:', error)
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
      
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: `Erro ao buscar distribuição de métodos: ${errorMessage}`,
        timer: 3000
      })
    } finally {
      setLoadingDistribution(false)
    }
  }, [filters.startDate, filters.endDate, filters.pluginspaceId]) */

  useEffect(() => {
    // Executar apenas na montagem inicial do componente
    // fetchS3Data será chamado através do botão ou quando necessário
    fetchAvailableMethods()
    fetchDollarRate()
  }, [fetchAvailableMethods, fetchDollarRate])

  // useEffect separado para carregar dados iniciais
  useEffect(() => {
    // Carregar dados iniciais apenas uma vez
    fetchS3Data()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Remover a aplicação manual de filtros já que agora a API faz isso
  useEffect(() => {
    // Como a API /accounting-s3/filter já retorna dados filtrados,
    // usamos diretamente os dados recebidos
    console.log('Setting filtered data:', data)
    setFilteredData(data)
  }, [data])

  // Gerar gráficos com base nos dados filtrados da API
  useEffect(() => {
    console.log('Generating charts - filteredData length:', filteredData.length, 'dollarRate:', dollarRate)
    if (filteredData.length > 0) {
      // Gráfico de consumo por PluginSpace (agrupamento por pluginspaceId)
      const pluginspaceData = filteredData.reduce((acc, item) => {
        const key = item.pluginspaceId.toString()
        // Converter string para número e somar
        const totalValue = parseFloat(String(item.total)) || 0
        acc[key] = (acc[key] || 0) + totalValue
        return acc
      }, {} as Record<string, number>)

      setConsumptionByPluginspace({
        title: { 
          text: i18n.t('s3Analytics.consumptionByPluginspace') as string,
          top: '20px',
          left: 'center'
        },
        tooltip: { 
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} GB ({d}%)'
        },
        series: [{
          name: 'Consumo',
          type: 'pie',
          radius: '70%',
          center: ['50%', '60%'],
          data: Object.entries(pluginspaceData).map(([key, value]) => ({
            name: `Pluginspace ${key}`,
            value: parseFloat(value.toFixed(9)) // Formatar para 9 casas decimais
          }))
        }]
      } as any)

      // Gráfico de distribuição de custos por métodos e pluginspaceId
      const methodData = filteredData.reduce((acc, item) => {
        // Calcular custo final: preço unitário × consumo
        const consumption = parseFloat(String(item.total)) || 0
        const unitPrice = parseFloat(String(item.priceUnit)) || 0
        const finalCost = consumption * unitPrice
        // Criar chave combinando método e pluginspaceId
        const key = `${item.method} - Pluginspace ${item.pluginspaceId}`
        acc[key] = (acc[key] || 0) + finalCost
        return acc
      }, {} as Record<string, number>)

      console.log('Method Final Cost Data for Chart (by method + pluginspace):', methodData) // Debug log

      setConsumptionByMethod({
        title: { 
          text: 'Análise de Custos por Método e Pluginspace',
          left: 'center',
          top: '20px'
        },
        tooltip: { 
          trigger: 'item',
          formatter: function(params: any) {
            const dollarValue = params.value
            const realValue = dollarValue * dollarRate
            return `${params.seriesName}<br/>${params.name}<br/>USD: $${dollarValue.toFixed(6)}<br/>BRL: R$ ${realValue.toFixed(6)}<br/>Percentual: ${params.percent}%`
          }
        },
        legend: {
          show: false // Desabilitar legenda para usar labels customizados
        },
        series: [{
          name: 'Custos por Método',
          type: 'pie',
          radius: ['30%', '70%'],
          center: ['50%', '60%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            position: 'outside',
            formatter: function(params: any) {
              const dollarValue = params.value
              const realValue = dollarValue * dollarRate
              const pluginspaceId = params.name.split('Pluginspace ')[1] || 'N/A'
              return `Pluginspace ${pluginspaceId}\nUSD: $${dollarValue.toFixed(4)}\nBRL: R$ ${realValue.toFixed(4)}\n${params.percent}%`
            },
            fontSize: 10,
            fontWeight: 'bold'
          },
          labelLine: {
            show: true,
            length: 15,
            length2: 25
          },
          data: Object.entries(methodData).map(([key, value]) => ({
            name: key,
            value: parseFloat(value.toFixed(9))
          }))
        }]
      } as any)

      // Gráfico temporal (análise por data)
      const sortedData = filteredData.sort((a, b) => 
        new Date(a.dataSumarized).getTime() - new Date(b.dataSumarized).getTime()
      )
      
      const timeData = sortedData.map(item => ({
        date: moment(item.dataSumarized).format('DD/MM'),
        value: parseFloat(String(item.total)) || 0
      }))

      setConsumptionByDate({
        title: { 
          text: i18n.t('s3Analytics.timeAnalysis') as string,
          top: '20px',
          left: 'center'
        },
        tooltip: { 
          trigger: 'axis',
          formatter: '{b}: {c} GB'
        },
        xAxis: { 
          type: 'category', 
          data: timeData.map(item => item.date)
        },
        yAxis: { type: 'value' },
        series: [{
          name: 'Consumo',
          type: 'line',
          data: timeData.map(item => parseFloat(item.value.toFixed(9))),
          smooth: true
        }]
      } as any)

      // Análise de custos por pluginspace (agrupamento por pluginspace e custo final)
      const costByPluginspace = filteredData.reduce((acc, item) => {
        const key = item.pluginspaceId.toString()
        // Calcular custo final: preço unitário × consumo
        const consumption = parseFloat(String(item.total)) || 0
        const unitPrice = parseFloat(String(item.priceUnit)) || 0
        const finalCost = consumption * unitPrice
        acc[key] = (acc[key] || 0) + finalCost
        return acc
      }, {} as Record<string, number>)

      setCostAnalysis({
        title: { 
          text: i18n.t('s3Analytics.costAnalysis') as string,
          top: '20px',
          left: 'center'
        },
        tooltip: { 
          trigger: 'axis',
          formatter: '{b}: R$ {c}'
        },
        xAxis: { 
          type: 'category', 
          data: Object.keys(costByPluginspace).map(id => `Pluginspace ${id}`)
        },
        yAxis: { type: 'value' },
        series: [{ 
          name: 'Custo', 
          type: 'bar', 
          data: Object.values(costByPluginspace).map(value => parseFloat(value.toFixed(9)))
        }]
      } as any)
    }
  }, [filteredData, dollarRate])

  // Análise IA dos dados
  const performAIAnalysis = () => {
    if (filteredData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: i18n.t('s3Analytics.noData') as string,
        text: 'Não há dados para análise.'
      })
      return
    }

    const totalConsumption = filteredData.reduce((sum, item) => sum + (parseFloat(String(item.total)) || 0), 0)
    const totalCostUSD = filteredData.reduce((sum, item) => {
      const consumption = parseFloat(String(item.total)) || 0
      const unitPrice = parseFloat(String(item.priceUnit)) || 0
      return sum + (consumption * unitPrice)
    }, 0)
    const totalCostBRL = totalCostUSD * dollarRate
    const avgConsumption = filteredData.length > 0 ? totalConsumption / filteredData.length : 0
    const topPluginspace = filteredData.reduce((acc, item) => {
      acc[item.pluginspaceId] = (acc[item.pluginspaceId] || 0) + (parseFloat(String(item.total)) || 0)
      return acc
    }, {} as Record<number, number>)
    
    const topPluginspaceId = Object.entries(topPluginspace)
      .sort(([,a], [,b]) => b - a)[0]?.[0]

    const analysis = `
      📊 Análise IA dos Dados S3:
      
      • Total de registros: ${filteredData.length}
      • Consumo total: ${totalConsumption.toFixed(9)} GB
      • Consumo médio: ${avgConsumption.toFixed(9)} GB
      • Custo total USD: $${totalCostUSD.toFixed(6)}
      • Custo total BRL: R$ ${totalCostBRL.toFixed(6)}
      • Cotação atual: R$ ${dollarRate.toFixed(4)} por USD
      • Pluginspace com maior consumo: ${topPluginspaceId}
      • Métodos mais utilizados: ${Object.keys(filteredData.reduce((acc, item) => {
        acc[item.method] = (acc[item.method] || 0) + 1
        return acc
      }, {} as Record<string, number>)).join(', ')}
      
      💡 Recomendações:
      - Monitore o Pluginspace ${topPluginspaceId} para otimizar custos
      - Considere implementar cache para reduzir requests
      - Analise padrões de uso por período
      - Acompanhe variações cambiais que impactam custos
    `

    // setAiAnalysis(analysis) - removido pois não está sendo usado
    
    Swal.fire({
      icon: 'info',
      title: 'Análise IA',
      html: analysis.replace(/\n/g, '<br>'),
      width: '600px'
    })
  }

  // Text-to-Speech
  const speakAnalysis = () => {
    console.log('TTS: Starting speakAnalysis function')
    console.log('TTS: filteredData.length:', filteredData.length)
    console.log('TTS: dollarRate:', dollarRate)
    console.log('TTS: typeof dollarRate:', typeof dollarRate)
    
    // Verificar se há dados na tela
    if (filteredData.length === 0) {
      console.log('TTS: No data available')
      Swal.fire({
        icon: 'warning',
        title: 'Sem Dados',
        text: 'Não há dados carregados para análise de voz. Carregue alguns dados primeiro.',
        timer: 3000
      })
      return
    }

    // Verificar se a cotação do dólar está disponível
    if (!dollarRate || dollarRate === 0 || dollarRate === null || isNaN(dollarRate)) {
      console.log('TTS: Dollar rate not available or invalid:', dollarRate)
      Swal.fire({
        icon: 'warning',
        title: 'Cotação Indisponível',
        text: 'Aguarde o carregamento da cotação do dólar ou atualize-a manualmente.',
        timer: 3000
      })
      return
    }

    // Verificar se o navegador suporta TTS
    if (!('speechSynthesis' in window)) {
      console.log('TTS: Browser does not support speech synthesis')
      Swal.fire({
        icon: 'error',
        title: 'Não Suportado',
        text: 'Text-to-speech não é suportado neste navegador.'
      })
      return
    }

    console.log('TTS: All validations passed, calculating values...')

    try {
      // Calcular valores para o TTS
      const totalConsumption = filteredData.reduce((sum, item) => {
        const value = parseFloat(String(item.total)) || 0
        return sum + value
      }, 0)
      
      const totalCostUSD = filteredData.reduce((sum, item) => {
        const consumption = parseFloat(String(item.total)) || 0
        const unitPrice = parseFloat(String(item.priceUnit)) || 0
        return sum + (consumption * unitPrice)
      }, 0)
      
      const totalCostBRL = totalCostUSD * dollarRate
      const uniquePluginspaces = new Set(filteredData.map(item => item.pluginspaceId)).size

      console.log('TTS: Calculated values:', {
        totalConsumption,
        totalCostUSD,
        totalCostBRL,
        uniquePluginspaces
      })

      // Verificar se os valores calculados são válidos
      if (isNaN(totalConsumption) || isNaN(totalCostUSD) || isNaN(totalCostBRL)) {
        console.log('TTS: Invalid calculated values (NaN detected)')
        Swal.fire({
          icon: 'warning',
          title: 'Dados Inválidos',
          text: 'Os dados carregados contêm valores inválidos para análise.',
          timer: 3000
        })
        return
      }

      if (totalConsumption === 0 && totalCostUSD === 0) {
        console.log('TTS: Zero values calculated')
        Swal.fire({
          icon: 'warning',
          title: 'Dados Inválidos',
          text: 'Os dados carregados não contêm valores válidos para análise.',
          timer: 3000
        })
        return
      }

      // Gerar texto para TTS com informações completas
      const text = `Análise dos dados S3: Total de ${filteredData.length} registros. Consumo total de ${totalConsumption.toFixed(2)} gigabytes. ${uniquePluginspaces} lojas únicas. Custo total final de ${totalCostUSD.toFixed(4)} dólares, equivalente a ${totalCostBRL.toFixed(2)} reais na cotação atual de ${dollarRate.toFixed(2)} reais por dólar.`
      
      console.log('TTS: Generated text:', text)
      
      // Verificar se o texto não está vazio
      if (!text || text.trim() === '') {
        console.log('TTS: Generated text is empty')
        Swal.fire({
          icon: 'error',
          title: 'Erro no TTS',
          text: 'Não foi possível gerar o texto para síntese de voz.',
          timer: 3000
        })
        return
      }
      
      // Parar qualquer síntese anterior
      if (window.speechSynthesis.speaking) {
        console.log('TTS: Cancelling previous speech')
        window.speechSynthesis.cancel()
        // Aguardar um pouco para garantir que foi cancelado
        setTimeout(() => {
          startSpeech(text)
        }, 100)
      } else {
        startSpeech(text)
      }
      
    } catch (error) {
      console.error('TTS: Error during calculation:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro no TTS',
        text: 'Erro durante o cálculo dos dados para síntese de voz.',
        timer: 3000
      })
    }
  }

  // Função auxiliar para iniciar a síntese de voz
  const startSpeech = (text: string) => {
    try {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = 'pt-BR'
      utterance.rate = 0.9
      utterance.volume = 0.8
      utterance.pitch = 1
      
      utterance.onstart = () => {
        console.log('TTS: Speech started successfully')
      }
      
      utterance.onend = () => {
        console.log('TTS: Speech ended successfully')
      }
      
      utterance.onerror = (event) => {
        console.error('TTS: Speech synthesis error:', event)
        console.error('TTS: Error details:', {
          error: event.error,
          elapsedTime: event.elapsedTime,
          name: event.name
        })
        
        Swal.fire({
          icon: 'error',
          title: 'Erro no TTS',
          text: `Erro na síntese de voz: ${event.error}. Tente novamente.`,
          timer: 3000
        })
      }
      
      console.log('TTS: Starting speech synthesis with utterance:', utterance)
      window.speechSynthesis.speak(utterance)
      
    } catch (error) {
      console.error('TTS: Error creating speech utterance:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro no TTS',
        text: 'Erro ao criar a síntese de voz. Verifique as configurações do navegador.',
        timer: 3000
      })
    }
  }

  // TTS para gráficos específicos
  const speakChartAnalysis = (chartType: string) => {
    if (!('speechSynthesis' in window)) {
      Swal.fire({
        icon: 'error',
        title: 'Não Suportado',
        text: 'Text-to-speech não é suportado neste navegador.'
      })
      return
    }

    if (filteredData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sem Dados',
        text: 'Não há dados carregados para análise de voz.',
        timer: 3000
      })
      return
    }

    let text = ''
    
    switch (chartType) {
      case 'pluginspace':
        const pluginspaceData = filteredData.reduce((acc, item) => {
          const key = item.pluginspaceId.toString()
          const totalValue = parseFloat(String(item.total)) || 0
          acc[key] = (acc[key] || 0) + totalValue
          return acc
        }, {} as Record<string, number>)
        
        const topPluginspace = Object.entries(pluginspaceData)
          .sort(([,a], [,b]) => b - a)[0]
        
        text = `Análise do gráfico de consumo por pluginspace: Temos ${Object.keys(pluginspaceData).length} pluginspaces únicos. O pluginspace ${topPluginspace[0]} tem o maior consumo com ${topPluginspace[1].toFixed(2)} gigabytes, representando ${((topPluginspace[1] / Object.values(pluginspaceData).reduce((a, b) => a + b, 0)) * 100).toFixed(1)} por cento do total.`
        break
        
      case 'method':
        const methodData = filteredData.reduce((acc, item) => {
          const consumption = parseFloat(String(item.total)) || 0
          const unitPrice = parseFloat(String(item.priceUnit)) || 0
          const finalCost = consumption * unitPrice
          const key = `${item.method} - Pluginspace ${item.pluginspaceId}`
          acc[key] = (acc[key] || 0) + finalCost
          return acc
        }, {} as Record<string, number>)
        
        const topMethod = Object.entries(methodData)
          .sort(([,a], [,b]) => b - a)[0]
        
        text = `Análise do gráfico de custos por método: Temos ${Object.keys(methodData).length} combinações de método e pluginspace. O maior custo é de ${topMethod[0]} com ${topMethod[1].toFixed(4)} dólares, equivalente a ${(topMethod[1] * dollarRate).toFixed(2)} reais.`
        break
        
      case 'timeline':
        const timeData = filteredData.sort((a, b) => 
          new Date(a.dataSumarized).getTime() - new Date(b.dataSumarized).getTime()
        )
        
        const totalDays = new Set(timeData.map(item => item.dataSumarized)).size
        const avgDailyConsumption = timeData.reduce((sum, item) => sum + (parseFloat(String(item.total)) || 0), 0) / totalDays
        
        text = `Análise temporal dos dados: O período analisado compreende ${totalDays} dias únicos. O consumo médio diário é de ${avgDailyConsumption.toFixed(2)} gigabytes. O período mostra ${timeData.length} registros de atividade.`
        break
        
      case 'cost':
        const costByPluginspace = filteredData.reduce((acc, item) => {
          const key = item.pluginspaceId.toString()
          const consumption = parseFloat(String(item.total)) || 0
          const unitPrice = parseFloat(String(item.priceUnit)) || 0
          const finalCost = consumption * unitPrice
          acc[key] = (acc[key] || 0) + finalCost
          return acc
        }, {} as Record<string, number>)
        
        const topCost = Object.entries(costByPluginspace)
          .sort(([,a], [,b]) => b - a)[0]
        
        const totalCost = Object.values(costByPluginspace).reduce((a, b) => a + b, 0)
        
        text = `Análise de custos por pluginspace: O custo total é de ${totalCost.toFixed(4)} dólares, equivalente a ${(totalCost * dollarRate).toFixed(2)} reais. O pluginspace ${topCost[0]} tem o maior custo individual com ${topCost[1].toFixed(4)} dólares.`
        break
        
      default:
        text = 'Tipo de gráfico não reconhecido para análise.'
    }

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel()
      setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.lang = 'pt-BR'
        utterance.rate = 0.9
        utterance.volume = 0.8
        utterance.pitch = 1
        
        utterance.onerror = (event) => {
          console.log('TTS Chart: Speech error event:', event.error)
          // Não mostrar erro se foi cancelado intencionalmente
          if (event.error !== 'canceled' && event.error !== 'interrupted') {
            Swal.fire({
              icon: 'error',
              title: 'Erro no TTS',
              text: `Erro na síntese de voz: ${event.error}. Tente novamente.`,
              timer: 3000
            })
          }
        }
        
        window.speechSynthesis.speak(utterance)
      }, 100)
    } else {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = 'pt-BR'
      utterance.rate = 0.9
      utterance.volume = 0.8
      utterance.pitch = 1
      
      utterance.onerror = (event) => {
        console.log('TTS Chart: Speech error event:', event.error)
        // Não mostrar erro se foi cancelado intencionalmente
        if (event.error !== 'canceled' && event.error !== 'interrupted') {
          Swal.fire({
            icon: 'error',
            title: 'Erro no TTS',
            text: `Erro na síntese de voz: ${event.error}. Tente novamente.`,
            timer: 3000
          })
        }
      }
      
      window.speechSynthesis.speak(utterance)
    }
  }

  // Função para fechar modal e limpar estados
  const closeIAModal = () => {
    // Parar TTS se estiver ativo
    if (isSpeaking) {
      window.speechSynthesis.cancel()
      setIsSpeaking(false)
    }
    
    // Parar Speech Recognition se estiver ativo
    if (isListening && speechRecognition) {
      speechRecognition.stop()
      setIsListening(false)
    }
    
    setShowIAModal(false)
  }

  // Função para abrir modal de análise IA
  const openIAModal = (chartType: string) => {
    let chartData = null
    
    switch (chartType) {
      case 'pluginspace':
        chartData = filteredData.reduce((acc, item) => {
          const key = item.pluginspaceId.toString()
          const totalValue = parseFloat(String(item.total)) || 0
          acc[key] = (acc[key] || 0) + totalValue
          return acc
        }, {} as Record<string, number>)
        break
        
      case 'method':
        chartData = filteredData.reduce((acc, item) => {
          const consumption = parseFloat(String(item.total)) || 0
          const unitPrice = parseFloat(String(item.priceUnit)) || 0
          const finalCost = consumption * unitPrice
          const key = `${item.method} - Pluginspace ${item.pluginspaceId}`
          acc[key] = (acc[key] || 0) + finalCost
          return acc
        }, {} as Record<string, number>)
        break
        
      case 'timeline':
        chartData = filteredData.sort((a, b) => 
          new Date(a.dataSumarized).getTime() - new Date(b.dataSumarized).getTime()
        ).map(item => ({
          date: item.dataSumarized,
          consumption: parseFloat(String(item.total)) || 0,
          pluginspace: item.pluginspaceId,
          method: item.method
        }))
        break
        
      case 'cost':
        chartData = filteredData.reduce((acc, item) => {
          const key = item.pluginspaceId.toString()
          const consumption = parseFloat(String(item.total)) || 0
          const unitPrice = parseFloat(String(item.priceUnit)) || 0
          const finalCost = consumption * unitPrice
          acc[key] = (acc[key] || 0) + finalCost
          return acc
        }, {} as Record<string, number>)
        break
    }
    
    setCurrentChartData(chartData)
    setCurrentChartType(chartType)
    setIaPrompt('')
    setIaAnalysisResult('')
    setShowIAModal(true)
  }

  // Função para enviar análise IA
  const submitIAAnalysis = async () => {
    if (!iaPrompt.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Prompt Necessário',
        text: 'Por favor, insira um prompt para análise.',
        timer: 3000
      })
      return
    }

    try {
      setIaAnalysisLoading(true)
      
      const requestBody = {
        prompt: iaPrompt,
        metadata: currentChartData,
        chartType: currentChartType,
        humanized: true,
        typeStore: 'normal'
      }
      
      console.log('Sending IA Analysis Request:', requestBody)
      
      const response = await POST('analyse-ia/analyse-grafic', requestBody)
      console.log('IA Analysis Response:', response)
      
      if (response.success && response.data) {
        // Extrair o texto da análise IA do retorno
        let analysisText = ''
        
        if (typeof response.data === 'string') {
          analysisText = response.data
        } else if (response.data.humanizedAnswer) {
          analysisText = response.data.humanizedAnswer
        } else if (response.data.result && response.data.result.humanizedAnswer) {
          analysisText = response.data.result.humanizedAnswer
        } else {
          analysisText = JSON.stringify(response.data, null, 2)
        }
        
        setIaAnalysisResult(analysisText)
        Swal.fire({
          icon: 'success',
          title: 'Análise Concluída',
          text: response.message || 'Análise IA realizada com sucesso!',
          timer: 2000
        })
      } else {
        throw new Error(response.message || 'Erro na análise IA')
      }
    } catch (error) {
      console.error('Error in IA Analysis:', error)
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido'
      
      Swal.fire({
        icon: 'error',
        title: 'Erro na Análise IA',
        text: `Erro: ${errorMessage}`,
        timer: 3000
      })
    } finally {
      setIaAnalysisLoading(false)
    }
  }

  // TTS para resultado da análise IA com toggle
  const toggleIAResultSpeech = () => {
    if (!iaAnalysisResult) {
      Swal.fire({
        icon: 'warning',
        title: 'Sem Resultado',
        text: 'Não há resultado de análise para reproduzir.',
        timer: 3000
      })
      return
    }

    if (!('speechSynthesis' in window)) {
      Swal.fire({
        icon: 'error',
        title: 'Não Suportado',
        text: 'Text-to-speech não é suportado neste navegador.'
      })
      return
    }

    if (isSpeaking) {
      // Parar a fala
      window.speechSynthesis.cancel()
      setIsSpeaking(false)
    } else {
      // Iniciar a fala
      const textToSpeak = typeof iaAnalysisResult === 'string' 
        ? iaAnalysisResult 
        : String(iaAnalysisResult)
      
      const utterance = new SpeechSynthesisUtterance(textToSpeak)
      utterance.lang = 'pt-BR'
      utterance.rate = 0.9
      utterance.volume = 0.8
      utterance.pitch = 1
      
      utterance.onstart = () => {
        console.log('TTS: Speech started successfully')
        setIsSpeaking(true)
      }
      
      utterance.onend = () => {
        console.log('TTS: Speech ended successfully')
        setIsSpeaking(false)
      }
      
      utterance.onerror = (event) => {
        console.log('TTS: Speech error event:', event.error)
        setIsSpeaking(false)
        
        // Não mostrar erro se foi cancelado intencionalmente
        if (event.error !== 'canceled' && event.error !== 'interrupted') {
          Swal.fire({
            icon: 'error',
            title: 'Erro no TTS',
            text: `Erro na síntese de voz: ${event.error}. Tente novamente.`,
            timer: 3000
          })
        }
      }
      
      window.speechSynthesis.speak(utterance)
    }
  }

  // Função para imprimir o resultado da análise IA
  const printIAResult = () => {
    if (!iaAnalysisResult) {
      Swal.fire({
        icon: 'warning',
        title: 'Sem Resultado',
        text: 'Não há resultado de análise para imprimir.',
        timer: 3000
      })
      return
    }

    const printWindow = window.open('', '_blank')
    if (printWindow) {
      const chartTypeName = 
        currentChartType === 'pluginspace' ? 'Consumo por Pluginspace' :
        currentChartType === 'method' ? 'Custos por Método' :
        currentChartType === 'timeline' ? 'Análise Temporal' :
        currentChartType === 'cost' ? 'Análise de Custos' : 'Gráfico'

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Análise IA - ${chartTypeName}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            h1 { 
              color: #333; 
              border-bottom: 2px solid #007bff;
              padding-bottom: 10px;
            }
            .date { 
              color: #666; 
              font-size: 14px; 
              margin-bottom: 20px;
            }
            .content { 
              white-space: pre-wrap; 
              margin-top: 20px;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>🤖 Análise IA Personalizada</h1>
          <div class="date">
            <strong>Gráfico:</strong> ${chartTypeName}<br>
            <strong>Data:</strong> ${new Date().toLocaleString('pt-BR')}<br>
            <strong>Sistema:</strong> S3 Analytics Dashboard
          </div>
          <div class="content">${typeof iaAnalysisResult === 'string' ? iaAnalysisResult : String(iaAnalysisResult)}</div>
        </body>
        </html>
      `
      
      printWindow.document.write(printContent)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
    }
  }

  // Função para inicializar o Speech Recognition
  const initializeSpeechRecognition = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      Swal.fire({
        icon: 'error',
        title: 'Não Suportado',
        text: 'Speech-to-text não é suportado neste navegador.'
      })
      return null
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    const recognition = new SpeechRecognition()
    
    recognition.continuous = false
    recognition.interimResults = false
    recognition.lang = 'pt-BR'
    
    recognition.onstart = () => {
      setIsListening(true)
    }
    
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript
      setIaPrompt(prev => prev + (prev ? ' ' : '') + transcript)
    }
    
    recognition.onerror = (event: any) => {
      setIsListening(false)
      console.error('Speech recognition error:', event.error)
      Swal.fire({
        icon: 'error',
        title: 'Erro no Reconhecimento',
        text: `Erro no reconhecimento de voz: ${event.error}`,
        timer: 3000
      })
    }
    
    recognition.onend = () => {
      setIsListening(false)
    }
    
    return recognition
  }

  // Toggle Speech-to-Text
  const toggleSpeechToText = () => {
    if (isListening) {
      // Parar o reconhecimento
      if (speechRecognition) {
        speechRecognition.stop()
      }
      setIsListening(false)
    } else {
      // Iniciar o reconhecimento
      let recognition = speechRecognition
      if (!recognition) {
        recognition = initializeSpeechRecognition()
        if (!recognition) return
        setSpeechRecognition(recognition)
      }
      
      try {
        recognition.start()
      } catch (error) {
        console.error('Error starting speech recognition:', error)
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao iniciar reconhecimento de voz.',
          timer: 3000
        })
      }
    }
  }

  // Exportar dados
  const exportData = () => {
    const csvContent = [
      ['ID', 'Método', 'PluginSpace', 'Folder', 'Consumo', 'Preço Unitário USD', 'Custo Final USD', 'Custo Final BRL', 'Data'].join(','),
      ...filteredData.map(item => {
        const consumption = parseFloat(String(item.total))
        const unitPrice = parseFloat(String(item.priceUnit))
        const finalCostUSD = consumption * unitPrice
        const finalCostBRL = finalCostUSD * dollarRate
        
        return [
          item.id,
          item.method,
          item.pluginspaceId,
          item.folder || '-',
          consumption.toFixed(9),
          unitPrice.toFixed(9),
          finalCostUSD.toFixed(9),
          finalCostBRL.toFixed(9),
          item.dataSumarized
        ].join(',')
      })
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `s3-analytics-${moment().format('YYYY-MM-DD')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  // Função para aplicar filtros (refaz a busca na API)
  const applyFilters = useCallback(() => {
    fetchS3Data()
  }, [fetchS3Data])

  // Limpar filtros
  const clearFilters = () => {
    setFilters({
      startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      pluginspaceId: '',
      method: '',
      folder: ''
    })
    // Após limpar os filtros, refazer a busca
    setTimeout(() => fetchS3Data(), 100)
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>{i18n.t('s3Analytics.title')}</PageTitle>
      
      <div className="container-fluid">
        <BreadCrumbs />
        
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">{i18n.t('s3Analytics.title')}</h3>
          </div>
          
          <div className="card-body">
            {loading && <Loading />}
            
            {/* Controles principais */}
            <div className="row mb-4">
              <div className="col-md-2">
                <Button 
                  text={loading ? "Carregando..." : "Buscar Dados"}
                  icon={loading ? "bi-arrow-repeat" : "bi-search"}
                  click={fetchS3Data}
                  color="primary"
                  size="small"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Filtros */}
            <div className="row mb-4">
              <div className="col-md-2">
                <label>{i18n.t('s3Analytics.method')}</label>
                <div className="position-relative">
                  <select 
                    className="form-control form-control-sm"
                    value={filters.method}
                    onChange={(e) => setFilters(prev => ({ ...prev, method: e.target.value }))}
                    style={{ 
                      appearance: 'none',
                      backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="">{i18n.t('s3Analytics.allMethods')}</option>
                    {availableMethods.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="col-md-2">
                <label>{i18n.t('s3Analytics.pluginspace')}</label>
                <input
                  type="text"
                  placeholder="Ex: 1001"
                  className="form-control form-control-sm"
                  value={filters.pluginspaceId}
                  onChange={(e) => setFilters(prev => ({ ...prev, pluginspaceId: e.target.value }))}
                />
              </div>
              
              <div className="col-md-1">
                <label>Folder</label>
                <div className="position-relative">
                  <select 
                    className="form-control form-control-sm"
                    value={filters.folder}
                    onChange={(e) => setFilters(prev => ({ ...prev, folder: e.target.value }))}
                    style={{ 
                      appearance: 'none',
                      backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3e%3c/svg%3e")',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="">Todos</option>
                    <option value="stock">Estoque</option>
                    <option value="price">Preço</option>
                    <option value="product">Produto</option>
                  </select>
                </div>
              </div>
              
              <div className="col-md-2">
                <label>{i18n.t('s3Analytics.startDate')}</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              
              <div className="col-md-2">
                <label>{i18n.t('s3Analytics.endDate')}</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>

              <div className="col-md-2 d-flex align-items-end gap-2">
                <Button 
                  text=""
                  icon="bi-funnel"
                  click={applyFilters}
                  color="primary"
                  size="small"
                />
                <Button 
                  text=""
                  icon="bi-eraser"
                  click={clearFilters}
                  color="secondary"
                  size="small"
                />
              </div>
            </div>

            {/* Ações adicionais */}
            <div className="row mb-4">
              <div className="col-md-2">
                <Button 
                  text="Análise IA"
                  icon="bi-robot"
                  click={performAIAnalysis}
                  color={filteredData.length > 0 ? "success" : "secondary"}
                  size="small"
                  disabled={filteredData.length === 0}
                />
                {filteredData.length === 0 && (
                  <small className="text-muted d-block mt-1">Sem dados</small>
                )}
              </div>
              
              <div className="col-md-2">
                <Button 
                  text="TTS"
                  icon="bi-volume-up"
                  click={speakAnalysis}
                  color={filteredData.length > 0 && dollarRate > 0 && !isNaN(dollarRate) ? "warning" : "secondary"}
                  size="small"
                  disabled={filteredData.length === 0 || !dollarRate || dollarRate === 0 || isNaN(dollarRate)}
                />
                {(filteredData.length === 0 || !dollarRate || dollarRate === 0 || isNaN(dollarRate)) && (
                  <small className="text-muted d-block mt-1">
                    {filteredData.length === 0 ? 'Sem dados' : 'Aguardando cotação'}
                  </small>
                )}
              </div>
              
              <div className="col-md-2">
                <Button 
                  text="Atualizar Dólar"
                  icon="bi-currency-dollar"
                  click={fetchDollarRate}
                  color="info"
                  size="small"
                />
              </div>
              
              <div className="col-md-2">
                <Button 
                  text="Exportar"
                  icon="bi-download"
                  click={exportData}
                  color={filteredData.length > 0 ? "primary" : "secondary"}
                  size="small"
                  disabled={filteredData.length === 0}
                />
                {filteredData.length === 0 && (
                  <small className="text-muted d-block mt-1">Sem dados</small>
                )}
              </div>
            </div>

            {/* Informações resumidas */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h5>{filteredData.length}</h5>
                    <small>{i18n.t('s3Analytics.totalRecords')}</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h5>{filteredData.reduce((sum, item) => sum + (parseFloat(String(item.total)) || 0), 0).toFixed(9)} GB</h5>
                    <small>{i18n.t('s3Analytics.totalConsumption')}</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h5>{new Set(filteredData.map(item => item.pluginspaceId)).size}</h5>
                    <small>{i18n.t('s3Analytics.uniquePluginspaces')}</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h5>USD $ {filteredData.reduce((sum, item) => {
                      const consumption = parseFloat(String(item.total)) || 0
                      const unitPrice = parseFloat(String(item.priceUnit)) || 0
                      return sum + (consumption * unitPrice)
                    }, 0).toFixed(6)}</h5>
                    <small>Custo Total (Final)</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Segunda linha de cards */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card bg-primary text-white">
                  <div className="card-body text-center">
                    <h5>R$ {dollarRate.toFixed(4)}</h5>
                    <small>Dólar Atual (USD/BRL)</small>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card bg-success text-white">
                  <div className="card-body text-center">
                    <h5>R$ {(filteredData.reduce((sum, item) => {
                      const consumption = parseFloat(String(item.total)) || 0
                      const unitPrice = parseFloat(String(item.priceUnit)) || 0
                      return sum + (consumption * unitPrice)
                    }, 0) * dollarRate).toFixed(6)}</h5>
                    <small>Custo Total em Reais</small>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card bg-info text-white">
                  <div className="card-body text-center">
                    <h5>Última atualização: {new Date().toLocaleString('pt-BR')}</h5>
                    <small>Cotação obtida via API externa</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Gráficos */}
            <div className="row">
              <div className="col-md-6 mb-4">
                {consumptionByPluginspace.title && (
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="card-title mb-0">Consumo por Pluginspace</h6>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => speakChartAnalysis('pluginspace')}
                          title="Ouvir análise do gráfico"
                        >
                          <i className="bi bi-volume-up fs-5"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          onClick={() => openIAModal('pluginspace')}
                          title="Análise IA personalizada"
                        >
                          <i className="bi bi-robot fs-5"></i>
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      <ReactECharts option={consumptionByPluginspace} style={{ height: '400px' }} />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="col-md-6 mb-4">
                {Object.keys(consumptionByMethod).length > 0 && consumptionByMethod.series && (
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="card-title mb-0">Custos por Método</h6>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => speakChartAnalysis('method')}
                          title="Ouvir análise do gráfico"
                        >
                          <i className="bi bi-volume-up fs-5"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          onClick={() => openIAModal('method')}
                          title="Análise IA personalizada"
                        >
                          <i className="bi bi-robot fs-5"></i>
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      <ReactECharts option={consumptionByMethod} style={{ height: '400px' }} />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="col-md-6 mb-4">
                {consumptionByDate.title && (
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="card-title mb-0">Análise Temporal</h6>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => speakChartAnalysis('timeline')}
                          title="Ouvir análise do gráfico"
                        >
                          <i className="bi bi-volume-up fs-5"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          onClick={() => openIAModal('timeline')}
                          title="Análise IA personalizada"
                        >
                          <i className="bi bi-robot fs-5"></i>
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      <ReactECharts option={consumptionByDate} style={{ height: '400px' }} />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="col-md-6 mb-4">
                {costAnalysis.title && (
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="card-title mb-0">Análise de Custos</h6>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => speakChartAnalysis('cost')}
                          title="Ouvir análise do gráfico"
                        >
                          <i className="bi bi-volume-up fs-5"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          onClick={() => openIAModal('cost')}
                          title="Análise IA personalizada"
                        >
                          <i className="bi bi-robot fs-5"></i>
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      <ReactECharts option={costAnalysis} style={{ height: '400px' }} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tabela de dados */}
            {filteredData.length > 0 && (
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header">
                      <h5>{i18n.t('s3Analytics.dataTable')}</h5>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-striped table-sm">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>{i18n.t('s3Analytics.method')}</th>
                              <th>{i18n.t('s3Analytics.pluginspace')}</th>
                              <th>Folder</th>
                              <th>{i18n.t('s3Analytics.consumption')}</th>
                              <th>Preço Unitário (USD)</th>
                              <th>Custo Final (USD)</th>
                              <th>Custo Final (BRL)</th>
                              <th>{i18n.t('s3Analytics.date')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredData.slice(0, 10).map((item) => {
                              console.log('Table row item:', item); // Debug log
                              const consumption = parseFloat(String(item.total)) || 0
                              const unitPrice = parseFloat(String(item.priceUnit)) || 0
                              const finalCostUSD = consumption * unitPrice
                              const finalCostBRL = finalCostUSD * dollarRate
                              
                              return (
                              <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>
                                  <span className={`badge badge-${
                                    item.method === 'GET' ? 'success' :
                                    item.method === 'POST' ? 'primary' :
                                    item.method === 'PUT' ? 'warning' : 'danger'
                                  }`}>
                                    {item.method}
                                  </span>
                                </td>
                                <td>{item.pluginspaceId}</td>
                                <td>
                                  <span className="text-muted">
                                    {item.folder || '-'}
                                  </span>
                                </td>
                                <td>{consumption.toFixed(9)} {item.unitMenssure}</td>
                                <td>$ {unitPrice.toFixed(9)}</td>
                                <td>
                                  <strong>$ {finalCostUSD.toFixed(9)}</strong>
                                </td>
                                <td>
                                  <strong className="text-success">R$ {finalCostBRL.toFixed(9)}</strong>
                                </td>
                                <td>{moment(item.dataSumarized).format('DD/MM/YYYY')}</td>
                              </tr>
                              )
                            })}
                          </tbody>
                        </table>
                        {filteredData.length > 10 && (
                          <p className="text-muted">
                            {i18n.t('s3Analytics.showingFirst10')} {i18n.t('s3Analytics.of')} {filteredData.length} {i18n.t('s3Analytics.records')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal de Análise IA Personalizada */}
        {showIAModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-robot me-2"></i>
                    Análise IA Personalizada - {
                      currentChartType === 'pluginspace' ? 'Consumo por Pluginspace' :
                      currentChartType === 'method' ? 'Custos por Método' :
                      currentChartType === 'timeline' ? 'Análise Temporal' :
                      currentChartType === 'cost' ? 'Análise de Custos' : 'Gráfico'
                    }
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={closeIAModal}
                  ></button>
                </div>
                
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="iaPrompt" className="form-label">
                      <i className="bi bi-chat-text me-2"></i>
                      Prompt para Análise IA:
                    </label>
                    <div className="input-group">
                      <textarea
                        id="iaPrompt"
                        className="form-control"
                        rows={4}
                        value={iaPrompt}
                        onChange={(e) => setIaPrompt(e.target.value)}
                        placeholder="Descreva que tipo de análise você gostaria que a IA faça com os dados deste gráfico..."
                        disabled={iaAnalysisLoading}
                      />
                      <button
                        type="button"
                        className={`btn ${isListening ? 'btn-danger' : 'btn-outline-secondary'}`}
                        onClick={toggleSpeechToText}
                        title={isListening ? 'Parar reconhecimento de voz' : 'Ativar reconhecimento de voz'}
                        disabled={iaAnalysisLoading}
                      >
                        <i className={`bi ${isListening ? 'bi-mic-fill' : 'bi-mic'}`}></i>
                      </button>
                    </div>
                    {isListening && (
                      <small className="text-danger">
                        <i className="bi bi-record-circle me-1"></i>
                        Fale agora... (o texto será adicionado ao prompt)
                      </small>
                    )}
                  </div>
                  
                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={submitIAAnalysis}
                      disabled={iaAnalysisLoading || !iaPrompt.trim()}
                    >
                      {iaAnalysisLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Analisando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Enviar para IA
                        </>
                      )}
                    </button>
                    
                    {iaAnalysisResult && (
                      <>
                        <button
                          type="button"
                          className={`btn ${isSpeaking ? 'btn-danger' : 'btn-warning'}`}
                          onClick={toggleIAResultSpeech}
                          title={isSpeaking ? 'Parar locução' : 'Ouvir resultado da análise'}
                        >
                          <i className={`bi ${isSpeaking ? 'bi-stop-fill' : 'bi-volume-up'} me-2`}></i>
                          {isSpeaking ? 'Parar' : 'Ouvir'}
                        </button>
                        
                        <button
                          type="button"
                          className="btn btn-info"
                          onClick={printIAResult}
                          title="Imprimir resultado da análise"
                        >
                          <i className="bi bi-printer me-2"></i>
                          Imprimir
                        </button>
                      </>
                    )}
                  </div>
                  
                  {iaAnalysisResult && (
                    <div className="mt-3">
                      <label className="form-label">
                        <i className="bi bi-lightbulb me-2"></i>
                        Resultado da Análise IA:
                      </label>
                      <div 
                        className="border rounded p-3 bg-light"
                        style={{ maxHeight: '300px', overflowY: 'auto' }}
                      >
                        <div dangerouslySetInnerHTML={{ 
                          __html: typeof iaAnalysisResult === 'string' 
                            ? iaAnalysisResult.replace(/\n/g, '<br>') 
                            : String(iaAnalysisResult).replace(/\n/g, '<br>')
                        }} />
                      </div>
                    </div>
                  )}
                  
                  {currentChartData && (
                    <div className="mt-3">
                      <details>
                        <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                          <i className="bi bi-info-circle me-2"></i>
                          Ver dados do gráfico enviados para IA
                        </summary>
                        <pre className="small text-muted mt-2 p-2 bg-light border rounded" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          {JSON.stringify(currentChartData, null, 2)}
                        </pre>
                      </details>
                    </div>
                  )}
                </div>
                
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={closeIAModal}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const S3Analytics: FC = () => {
  return <S3AnalyticsContent />
}

export default S3Analytics
